import {
  Button,
  Col,
  Form,
  Grid,
  Input,
  Row,
  Select,
  Space,
  notification,
  Typography,
} from 'antd';
import {
  getFanInitialValues,
  useCountriesWithTranslations,
  useUpdateFanProfile,
  useUserProfile,
} from '@seaters/data-access';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { StepItemContentProps } from './types';
import { FanValidator, FanValidatorSchema } from '@seaters/constants';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { AccessibleSelect, PhoneInputWithCountryCode } from '@seaters/ui';
import { useEffect, useState } from 'react';
import { AccessibleRequiredMark } from '@seaters/ui';

const { useBreakpoint } = Grid;
const { Text } = Typography;

export function PersonalInfoForm({ ...props }: StepItemContentProps) {
  const { t } = useTranslation();
  const { data: fanProfile } = useUserProfile(true);
  const { xs } = useBreakpoint();

  const { mutate: updateFan } = useUpdateFanProfile();

  const initialValues = getFanInitialValues(fanProfile);
  const [phoneNumber, setPhoneNumber] = useState(
    initialValues
      ? initialValues?.countryCallingCode + initialValues?.localNumber
      : ''
  );

  const [countryCode, setCountryCode] = useState(
    initialValues ? initialValues?.countryCallingCode : ''
  );

  const { countriesOptions } = useCountriesWithTranslations({
    itemOffset: 0,
    maxPageSize: 240,
  });

  // `getFanInitialValues` also flattens phone/address parts for the other
  // profile screens; this form only edits the validator's own fields, so it is
  // seeded with just those.
  const formValues: FanValidator = {
    email: fanProfile?.email ?? '',
    firstName: fanProfile?.firstName,
    lastName: fanProfile?.lastName,
    language: fanProfile?.language ?? '',
    mobilePhoneNumber: fanProfile?.mobilePhoneNumber ?? null,
    personalInfo: {
      address: {
        ...fanProfile?.personalInfo?.address,
        zipCode: fanProfile?.personalInfo?.address?.zipCode,
        line1: fanProfile?.personalInfo?.address?.line1 ?? '',
        // The profile entity uses null for "not set"; the form uses undefined.
        country: fanProfile?.personalInfo?.address?.country ?? undefined,
        countryCode:
          fanProfile?.personalInfo?.address?.countryCode ?? undefined,
      },
    },
  };

  const methods = useForm<FanValidator>({
    mode: 'onBlur',
    resolver: zodResolver(FanValidatorSchema),
    defaultValues: formValues,
    values: formValues,
  });

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = methods;

  const onSubmit = async (values: FanValidator) => {
    const parsed = FanValidatorSchema.safeParse(values);

    if (!parsed.success) {
      console.log(parsed.error);
      return;
    }
    const {
      email,
      firstName,
      lastName,
      language,
      mobilePhoneNumber,
      personalInfo,
    } = parsed.data;

    const dataToSend = {
      ...initialValues,
      email,
      firstName,
      lastName,
      language,
      mobilePhoneNumber,
      personalInfo: {
        ...initialValues.personalInfo,
        address: personalInfo?.address,
      },
    };
    if (parsed.success) {
      updateFan(dataToSend, {
        onSuccess: () => {
          if (props.next) {
            props.next();
          }
        },
        onError: (err) => {
          console.error(err);
          // a rejected phone fails the whole write, so a generic message leaves the fan
          // with no idea which field blocked it
          const serverMessage = err.response?.data?.message;
          notification.error({
            message: serverMessage
              ? t(serverMessage)
              : t('notification_error_fan_profile_updated'),
          });
        },
      });
    }
  };

  useEffect(() => {
    if (phoneNumber) {
      setValue(
        'mobilePhoneNumber.localNumber',
        phoneNumber?.slice(countryCode?.length)
      );
    }
    if (countryCode) {
      setValue('mobilePhoneNumber.countryCallingCode', countryCode);
    }
  }, [phoneNumber, countryCode]);

  const inputSize = 'middle';
  const formClassName = 'w-full';
  const formItemClassName = '!mb-4';

  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: xs ? 'center' : 'flex-start',
        flexDirection: xs ? 'column' : 'row',
      }}
    >
      <FormProvider {...methods}>
        <Form
          className={formClassName}
          layout="vertical"
          onFinish={handleSubmit(onSubmit)}
          autoComplete="off"
          requiredMark={AccessibleRequiredMark}
        >
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                className={formItemClassName}
                name={`firstName`}
                label={
                  <Text>
                    {t('settings_personal-section_form_firstname-label')}
                  </Text>
                }
                htmlFor="firstName"
                validateStatus={errors.firstName ? 'error' : undefined}
                help={
                  errors.firstName
                    ? t(errors.firstName.message as string)
                    : undefined
                }
                required
              >
                <Controller
                  control={control}
                  name={`firstName`}
                  render={({ field }) => (
                    <Input id="firstName" {...field} size={inputSize} />
                  )}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                className={formItemClassName}
                name={`lastName`}
                label={
                  <Text>
                    {t('settings_personal-section_form_lastname-label')}
                  </Text>
                }
                htmlFor="lastName"
                validateStatus={errors.lastName ? 'error' : undefined}
                help={
                  errors.lastName
                    ? t(errors.lastName.message as string)
                    : undefined
                }
                required
              >
                <Controller
                  control={control}
                  name={`lastName`}
                  render={({ field }) => (
                    <Input id="lastName" {...field} size={inputSize} />
                  )}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                className={formItemClassName}
                label={<Text>{t('settings_phone_form_phone-label')}</Text>}
                name="mobilePhoneNumber.localNumber"
                htmlFor="phoneNumber"
                validateStatus={
                  errors.mobilePhoneNumber?.localNumber ? 'error' : undefined
                }
                help={
                  errors.mobilePhoneNumber?.localNumber
                    ? t(errors.mobilePhoneNumber.localNumber.message as string)
                    : undefined
                }
              >
                <PhoneInputWithCountryCode
                  phoneNumber={phoneNumber}
                  setPhoneNumber={setPhoneNumber}
                  countryCode={countryCode}
                  setCountryCode={setCountryCode}
                  size={inputSize}
                  isError={!!errors.mobilePhoneNumber?.localNumber}
                  inputId="phoneNumber"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24}>
              <Form.Item
                className={formItemClassName}
                name={`personalInfo.address.line1`}
                label={
                  <Text>
                    {t('settings_personal-information_address-label-line1')}
                  </Text>
                }
                htmlFor="addressLine1"
                validateStatus={
                  errors.personalInfo?.address?.line1 ? 'error' : undefined
                }
                help={
                  errors.personalInfo?.address?.line1
                    ? t(errors.personalInfo.address.line1.message as string)
                    : undefined
                }
                required
              >
                <Controller
                  control={control}
                  name={`personalInfo.address.line1`}
                  render={({ field }) => (
                    <Input
                      id="addressLine1"
                      {...field}
                      size={inputSize}
                      data-testid="addressLine1Field"
                    />
                  )}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24}>
              <Form.Item
                className={formItemClassName}
                name={`personalInfo.address.line2`}
                label={
                  <Text>
                    {`${t(
                      'settings_personal-information_address-label-line2'
                    )} (${t('general_label_optional')})`}
                  </Text>
                }
                validateStatus={
                  errors.personalInfo?.address?.line2 ? 'error' : undefined
                }
                help={
                  errors.personalInfo?.address?.line2
                    ? t(errors.personalInfo.address.line2.message as string)
                    : undefined
                }
                htmlFor="addressLine2"
              >
                <Controller
                  control={control}
                  name={`personalInfo.address.line2`}
                  render={({ field }) => (
                    <Input
                      id="addressLine2"
                      {...field}
                      value={field.value ?? ''}
                      size={inputSize}
                    />
                  )}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={12}>
              <Form.Item
                className={formItemClassName}
                name={`personalInfo.address.city`}
                label={<Text>{t('addressCity')}</Text>}
                htmlFor="addressCity"
                validateStatus={
                  errors.personalInfo?.address?.city ? 'error' : undefined
                }
                help={
                  errors.personalInfo?.address?.city
                    ? t(errors.personalInfo.address.city.message as string)
                    : undefined
                }
                required
              >
                <Controller
                  control={control}
                  name={`personalInfo.address.city`}
                  render={({ field }) => (
                    <Input
                      id="addressCity"
                      {...field}
                      value={field.value ?? ''}
                      size={inputSize}
                      data-testid="addressCityField"
                    />
                  )}
                />
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item
                className={formItemClassName}
                name={`personalInfo.address.zipCode`}
                label={
                  <Text>
                    {t('settings_personal-information_postal-code-label')}
                  </Text>
                }
                htmlFor="zipCode"
                validateStatus={
                  errors.personalInfo?.address?.zipCode ? 'error' : undefined
                }
                help={
                  errors.personalInfo?.address?.zipCode
                    ? t(errors.personalInfo.address.zipCode.message as string)
                    : undefined
                }
                required
              >
                <Controller
                  control={control}
                  name={`personalInfo.address.zipCode`}
                  render={({ field }) => (
                    <Input
                      id="zipCode"
                      {...field}
                      value={field.value ?? ''}
                      size={inputSize}
                      data-testid="zipCodeField"
                    />
                  )}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={12}>
              <Form.Item
                className={formItemClassName}
                name={`personalInfo.address.state`}
                label={<Text>{t('checkout_att_field_state')}</Text>}
                htmlFor="addressState"
                validateStatus={
                  errors.personalInfo?.address?.state ? 'error' : undefined
                }
                help={
                  errors.personalInfo?.address?.state
                    ? t(errors.personalInfo.address.state.message as string)
                    : undefined
                }
              >
                <Controller
                  control={control}
                  name={`personalInfo.address.state`}
                  render={({ field }) => (
                    <Input
                      id="addressState"
                      {...field}
                      value={field.value ?? ''}
                      size={inputSize}
                    />
                  )}
                />
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item
                className={formItemClassName}
                name={`personalInfo.address.countryCode`}
                label={
                  <Text>
                    {t('settings_personal-information_country-label')}
                  </Text>
                }
                htmlFor="countryCode"
                validateStatus={
                  errors.personalInfo?.address?.countryCode
                    ? 'error'
                    : undefined
                }
                help={
                  errors.personalInfo?.address?.countryCode
                    ? t(
                        errors.personalInfo.address.countryCode
                          .message as string
                      )
                    : undefined
                }
                required
              >
                <Controller
                  control={control}
                  name={`personalInfo.address.countryCode`}
                  render={({ field }) => (
                    <AccessibleSelect
                      id="countryCode"
                      {...field}
                      size={inputSize}
                      showSearch
                      options={countriesOptions}
                    />
                  )}
                />
              </Form.Item>
            </Col>
          </Row>
          {xs ? (
            <Button
              htmlType="submit"
              type="primary"
              block
              size={inputSize}
              data-testid="checkoutSubmitButton"
            >
              {t('button_text_next')}
            </Button>
          ) : (
            <Space>
              <Button
                htmlType="submit"
                type="primary"
                size={inputSize}
                data-testid="checkoutSubmitButton"
              >
                {t('button_text_next')}
              </Button>
              <Button type="default" onClick={props.prev} size={inputSize}>
                {t('general_cancel-btn')}
              </Button>
            </Space>
          )}
        </Form>
      </FormProvider>
    </div>
  );
}
